// Stories — route /stories
//
// storygen surface (R2). Left column: a concept→shooting-plan create form plus
// the recent-story list. Right column: the selected story's shooting plan,
// rendered as a character roster + per-scene breakdown. Generation is
// synchronous — the "스토리 생성" button blocks while the planner→director→critic
// chain runs, so it shows a loading state until the persisted row returns.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Pencil, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import type { Character, ShootingPlan, Story, StoryFormat, StoryStatus } from '@/api/types'
import {
  useCreateStory,
  useDeleteStory,
  useRetryStory,
  useStories,
  useStory,
  useUpdateStory,
  useUpdateStoryPlan,
} from '@/hooks/useStories'
import { useCharacters } from '@/hooks/useCharacters'
import type { StoryCharacter, StoryScene } from '@/api/types'
import { cn, formatTime } from '@/lib/utils'
import type { BadgeVariant } from '@/admin-design/_common'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormRow,
  Input,
  Select,
  Tabs,
  Textarea,
} from '@/admin-design/_common'

// StorySource toggles the create form between a hand-typed concept and seeding
// from an existing script/storyboard (reuses the /scripts list).
type StorySource = 'concept' | 'script'

const STATUS_VARIANT: Record<StoryStatus, BadgeVariant> = {
  draft: 'muted',
  generating: 'info',
  ready: 'success',
  error: 'destructive',
}

export default function Stories() {
  const [source, setSource] = useState<StorySource>('concept')
  const [concept, setConcept] = useState('')
  const [scriptId, setScriptId] = useState<number | null>(null)
  const [format, setFormat] = useState<StoryFormat>('short')
  const [tone, setTone] = useState('')
  const [locale, setLocale] = useState('ko')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const storiesQ = useStories()
  const createM = useCreateStory()

  // Scripts list for the "대본에서 시드" picker — same query key as the /scripts
  // route so the cache is shared. Only fetched when the seed tab is active.
  const scriptsQ = useQuery({
    queryKey: ['scripts'],
    queryFn: api.listScripts,
    enabled: source === 'script',
  })

  const valid = selectedId != null && selectedId > 0
  const storyQ = useStory(selectedId ?? 0, valid)

  const seedMode = source === 'script'
  // Concept mode needs a concept; seed mode needs a selected script (concept
  // optional — the backend derives it from the script when blank).
  const canSubmit = seedMode ? scriptId != null : concept.trim().length > 0

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (createM.isPending || !canSubmit) return
    createM.mutate(
      {
        concept: concept.trim() || undefined,
        script_id: seedMode && scriptId != null ? scriptId : undefined,
        format,
        tone: tone.trim() || undefined,
        locale: locale.trim() || undefined,
      },
      { onSuccess: (data) => setSelectedId(data.id) },
    )
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Stories</h1>
        <span className="text-sm text-muted-foreground">{storiesQ.data?.length ?? 0}개</span>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4">
        <aside className="space-y-3">
          {/* Create form */}
          <Card>
            <Card.Header>
              <Card.Title className="text-sm">새 스토리</Card.Title>
              <Card.Description>
                컨셉을 직접 입력하거나 기존 대본에서 시드해 슈팅 플랜을 생성합니다.
              </Card.Description>
            </Card.Header>
            <Card.Body>
              <Tabs
                aria-label="스토리 시드 방식"
                className="mb-3"
                value={source}
                onChange={(k) => setSource(k as StorySource)}
                items={[
                  { key: 'concept', label: '컨셉 직접 입력' },
                  { key: 'script', label: '대본에서 시드' },
                ]}
              />
              <form className="space-y-3" onSubmit={onSubmit}>
                {seedMode ? (
                  <>
                    <FormRow
                      label="대본"
                      required
                      stacked
                      help="기존 스크립트의 제목·드래프트·콘티를 컨셉/컨텍스트로 사용합니다."
                    >
                      {scriptsQ.isLoading ? (
                        <p className="text-xs text-muted-foreground">대본 목록 불러오는 중…</p>
                      ) : scriptsQ.isError ? (
                        <p className="text-xs text-destructive">
                          대본 로드 실패: {errorMessage(scriptsQ.error)}
                        </p>
                      ) : (
                        <Select
                          value={scriptId != null ? String(scriptId) : ''}
                          onValueChange={(v) => setScriptId(v ? Number(v) : null)}
                          placeholder={
                            (scriptsQ.data?.length ?? 0) === 0 ? '등록된 대본 없음' : '대본 선택'
                          }
                          disabled={(scriptsQ.data?.length ?? 0) === 0}
                          options={(scriptsQ.data ?? []).map((p) => ({
                            label: p.title || `script #${p.id}`,
                            value: String(p.id),
                          }))}
                        />
                      )}
                    </FormRow>
                    <FormRow
                      label="컨셉"
                      stacked
                      help="비워두면 대본 제목/내용으로 컨셉을 만듭니다. (선택)"
                    >
                      <Textarea
                        value={concept}
                        onChange={(e) => setConcept(e.target.value)}
                        rows={3}
                        placeholder="대본을 어떤 방향으로 각색할지 (선택)"
                      />
                    </FormRow>
                  </>
                ) : (
                  <FormRow label="컨셉" required stacked>
                    <Textarea
                      value={concept}
                      onChange={(e) => setConcept(e.target.value)}
                      rows={4}
                      placeholder="예: 호기심 많은 고양이가 비 오는 도시를 탐험한다"
                      required
                    />
                  </FormRow>
                )}
                <FormRow label="포맷" stacked>
                  <Select
                    value={format}
                    onValueChange={(v) => setFormat(v as StoryFormat)}
                    options={[
                      { label: 'Short (1–3컷)', value: 'short' },
                      { label: 'Narrative (막/씬)', value: 'narrative' },
                    ]}
                  />
                </FormRow>
                <FormRow label="톤" stacked help="비워두면 기본값을 사용합니다.">
                  <Input
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    placeholder="예: 잔잔하고 따뜻한"
                  />
                </FormRow>
                <FormRow label="로케일" stacked>
                  <Input
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    placeholder="ko"
                  />
                </FormRow>
                {createM.isError && (
                  <p className="rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
                    {errorMessage(createM.error)}
                  </p>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  className="w-full"
                  loading={createM.isPending}
                  disabled={createM.isPending || !canSubmit}
                >
                  <Sparkles size={14} /> 스토리 생성
                </Button>
              </form>
            </Card.Body>
          </Card>

          {/* Recent stories */}
          <Card className="overflow-hidden">
            <Card.Header>
              <Card.Title className="text-sm">최근 스토리</Card.Title>
            </Card.Header>
            {storiesQ.isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">불러오는 중…</p>
            ) : storiesQ.isError ? (
              <p className="p-4 text-sm text-destructive">
                로드 실패: {errorMessage(storiesQ.error)}
              </p>
            ) : !storiesQ.data || storiesQ.data.length === 0 ? (
              <EmptyState
                title="아직 생성된 스토리가 없어요."
                description="왼쪽 폼에서 첫 스토리를 만들어보세요."
              />
            ) : (
              <ul className="divide-y divide-border">
                {storiesQ.data.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      className={cn(
                        'w-full text-left px-4 py-3 hover:bg-muted',
                        selectedId === s.id && 'bg-muted',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm text-card-foreground">
                          {s.shooting_plan?.title || s.concept || `story #${s.id}`}
                        </span>
                        <Badge shape="square" variant={STATUS_VARIANT[s.status] ?? 'muted'}>
                          {s.status}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {formatTime(s.updated_at)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>

        <main>
          <StoryDetail
            selected={valid}
            loading={storyQ.isLoading}
            isError={storyQ.isError}
            error={storyQ.error}
            story={storyQ.data}
            onDeleted={() => setSelectedId(null)}
          />
        </main>
      </div>
    </div>
  )
}

interface StoryDetailProps {
  selected: boolean
  loading: boolean
  isError: boolean
  error: unknown
  story?: Story
  onDeleted: () => void
}

function StoryDetail({ selected, loading, isError, error, story, onDeleted }: StoryDetailProps) {
  const [editing, setEditing] = useState(false)
  const [draftConcept, setDraftConcept] = useState('')
  const retryM = useRetryStory()
  const updateM = useUpdateStory()
  const deleteM = useDeleteStory()
  const busy = retryM.isPending || updateM.isPending || deleteM.isPending

  if (!selected) {
    return (
      <Card>
        <EmptyState
          title="선택된 스토리가 없어요."
          description="스토리를 생성하거나 왼쪽 목록에서 선택하면 슈팅 플랜이 여기에 표시됩니다."
        />
      </Card>
    )
  }
  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">불러오는 중…</p>
  }
  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return <p className="p-6 text-sm text-foreground">이 스토리는 존재하지 않거나 삭제되었어요.</p>
    }
    return <p className="p-6 text-sm text-destructive">로드 실패: {errorMessage(error)}</p>
  }
  if (!story) {
    return null
  }

  const plan = story.shooting_plan
  const generating = story.status === 'generating'

  const startEdit = () => {
    setDraftConcept(story.concept)
    setEditing(true)
  }
  const saveEdit = () => {
    if (!draftConcept.trim()) return
    updateM.mutate(
      { id: story.id, input: { concept: draftConcept.trim() } },
      { onSuccess: () => setEditing(false) },
    )
  }
  const onDelete = () => {
    if (!window.confirm('이 스토리를 삭제할까요? 되돌릴 수 없습니다.')) return
    deleteM.mutate(story.id, { onSuccess: onDeleted })
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-foreground">
          {plan?.title || story.concept || `story #${story.id}`}
        </h2>
        <Badge shape="square" variant={STATUS_VARIANT[story.status] ?? 'muted'}>
          {story.status}
        </Badge>
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={retryM.isPending}
            disabled={busy || generating}
            onClick={() => retryM.mutate(story.id)}
            title="저장된 컨셉으로 다시 생성"
          >
            <RefreshCw size={14} /> 재생성
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || generating}
            onClick={startEdit}
          >
            <Pencil size={14} /> 컨셉 수정
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={deleteM.isPending}
            disabled={busy || generating}
            onClick={onDelete}
          >
            <Trash2 size={14} /> 삭제
          </Button>
        </div>
      </header>

      {editing && (
        <Card>
          <Card.Body className="space-y-2">
            <FormRow label="컨셉 수정" stacked help="저장하면 draft로 초기화 — 재생성으로 다시 만드세요.">
              <Textarea
                value={draftConcept}
                onChange={(e) => setDraftConcept(e.target.value)}
                rows={4}
                autoFocus
              />
            </FormRow>
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
                취소
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={updateM.isPending}
                disabled={!draftConcept.trim()}
                onClick={saveEdit}
              >
                저장
              </Button>
            </div>
            {updateM.isError && (
              <p className="text-[11px] text-destructive">{errorMessage(updateM.error)}</p>
            )}
          </Card.Body>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <MetaCard label="Concept">{plan?.concept || story.concept}</MetaCard>
        <MetaCard label="Format">{plan?.format || story.format}</MetaCard>
        <MetaCard label="Locale">{plan?.locale || story.locale}</MetaCard>
      </div>

      {retryM.isError && (
        <p className="rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
          재생성 실패: {errorMessage(retryM.error)}
        </p>
      )}

      {story.status === 'error' && (
        <div className="space-y-1 rounded border border-destructive-soft bg-destructive-soft p-3 text-sm text-destructive-soft-foreground">
          <p>생성에 실패했어요. 컨셉을 수정하거나 재생성해보세요.</p>
          {story.error && (
            <p className="break-words font-mono text-[11px] opacity-80">{story.error}</p>
          )}
        </div>
      )}

      {!plan ? (
        story.status === 'generating' ? (
          <Card>
            <Card.Body>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                스토리 생성 중… (planner→director→critic, 수 분 걸릴 수 있어요)
              </div>
            </Card.Body>
          </Card>
        ) : (
          story.status !== 'error' && (
            <Card>
              <Card.Body>
                <p className="text-sm text-muted-foreground">
                  슈팅 플랜이 아직 없어요 (status: {story.status}).
                </p>
              </Card.Body>
            </Card>
          )
        )
      ) : (
        <ShootingPlanView storyId={story.id} plan={plan} />
      )}
    </div>
  )
}

const PLAN_ASPECT_OPTIONS = [
  { label: 'portrait', value: 'portrait' },
  { label: 'landscape', value: 'landscape' },
  { label: 'square', value: 'square' },
]

function ShootingPlanView({ storyId, plan }: { storyId: number; plan: ShootingPlan }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<ShootingPlan>(plan)
  const updatePlanM = useUpdateStoryPlan()
  const charactersQ = useCharacters()

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(plan)) as ShootingPlan)
    setEditing(true)
  }
  const save = () =>
    updatePlanM.mutate({ id: storyId, plan: draft }, { onSuccess: () => setEditing(false) })

  const setChar = (i: number, patch: Partial<StoryCharacter>) =>
    setDraft((d) => ({
      ...d,
      characters: d.characters.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }))
  const setScene = (i: number, patch: Partial<StoryScene>) =>
    setDraft((d) => ({
      ...d,
      scenes: d.scenes.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }))

  const view = editing ? draft : plan
  const charOptions = view.characters.map((c) => ({ label: c.id, value: c.id }))
  const generatedCharacterOptions = [
    { label: '미매칭', value: '0' },
    ...(charactersQ.data ?? []).map((c) => ({
      label: `${characterOptionName(c)} · ${c.status} · ${c.subject_prompt.slice(0, 42)}`,
      value: String(c.id),
      disabled: c.status !== 'ready' || !c.image_path,
    })),
  ]
  const generatedById = new Map((charactersQ.data ?? []).map((c) => [c.id, c]))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">슈팅 플랜</span>
        {editing ? (
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              취소
            </Button>
            <Button type="button" variant="primary" size="sm" loading={updatePlanM.isPending} onClick={save}>
              저장
            </Button>
          </div>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={startEdit}>
            <Pencil size={14} /> 씬 수정
          </Button>
        )}
      </div>
      {updatePlanM.isError && (
        <p className="rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
          저장 실패: {errorMessage(updatePlanM.error)}
        </p>
      )}

      {/* Characters */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">
          캐릭터 <span className="text-muted-foreground">({view.characters.length})</span>
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {view.characters.map((c, i) => (
            <Card key={i}>
              <Card.Body className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm text-card-foreground">{c.id}</span>
                  {editing ? (
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      <Select
                        value={String(c.character_id ?? 0)}
                        onValueChange={(v) => setChar(i, { character_id: Number(v) || undefined })}
                        options={generatedCharacterOptions}
                      />
                      <Select
                        value={c.aspect}
                        onValueChange={(v) => setChar(i, { aspect: v })}
                        options={PLAN_ASPECT_OPTIONS}
                      />
                      <Input
                        value={String(c.seed)}
                        onChange={(e) => setChar(i, { seed: Number(e.target.value) || 0 })}
                        inputMode="numeric"
                        className="w-24"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {c.character_id ? (
                        <Badge shape="square" variant="success">char #{c.character_id}</Badge>
                      ) : (
                        <Badge shape="square" variant="muted">미매칭</Badge>
                      )}
                      <Badge shape="square" variant="muted">{c.aspect}</Badge>
                      <Badge shape="square" variant="muted">seed {c.seed}</Badge>
                    </div>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <PlanEditField
                      label="Persona"
                      value={c.persona ?? ''}
                      onChange={(v) => setChar(i, { persona: v })}
                      rows={2}
                    />
                    <PlanEditField
                      label="Prompt"
                      value={c.krea2_prompt}
                      onChange={(v) => setChar(i, { krea2_prompt: v })}
                      rows={3}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {c.character_id && generatedById.get(c.character_id)?.image_path && (
                      <img
                        src={generatedById.get(c.character_id)?.image_path}
                        alt=""
                        className="aspect-square w-20 rounded border border-border object-cover"
                      />
                    )}
                    {c.persona && (
                      <p className="whitespace-pre-wrap text-xs leading-relaxed text-card-foreground">
                        {c.persona}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                      {c.krea2_prompt}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      </section>

      {/* Scenes */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">
          씬 <span className="text-muted-foreground">({view.scenes.length})</span>
        </h3>
        <div className="space-y-3">
          {view.scenes.map((sc, i) => (
            <Card key={i}>
              <Card.Body className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-card-foreground">씬 {sc.id}</span>
                  {editing ? (
                    <div className="flex items-center gap-1">
                      <Select
                        value={sc.character_ref}
                        onValueChange={(v) => setScene(i, { character_ref: v })}
                        options={charOptions}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      {sc.character_ref && (
                        <Badge shape="square" variant="info">{sc.character_ref}</Badge>
                      )}
                      <span className="font-mono text-[11px] text-muted-foreground">{sc.length_s}s</span>
                    </div>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <PlanEditField label="Visual" value={sc.visual} onChange={(v) => setScene(i, { visual: v })} rows={3} />
                    <PlanEditField label="Voice" value={sc.voice} onChange={(v) => setScene(i, { voice: v })} rows={2} />
                    <PlanEditField label="Sounds" value={sc.sounds} onChange={(v) => setScene(i, { sounds: v })} rows={2} />
                    <PlanEditField label="Action" value={sc.action} onChange={(v) => setScene(i, { action: v })} rows={1} />
                    <PlanEditField label="Camera" value={sc.camera} onChange={(v) => setScene(i, { camera: v })} rows={1} />
                  </div>
                ) : (
                  <>
                    <SceneField label="Visual">{sc.visual}</SceneField>
                    <SceneField label="Voice">{sc.voice || '—'}</SceneField>
                    <SceneField label="Sounds">{sc.sounds || '—'}</SceneField>
                    <SceneField label="Action">{sc.action || '—'}</SceneField>
                    <SceneField label="Camera">{sc.camera || '—'}</SceneField>
                  </>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      </section>
    </div>
  )
}

function characterOptionName(c: Character): string {
  const name = c.name?.trim()
  return name ? `${name} (#${c.id})` : `#${c.id}`
}

// PlanEditField — a labeled textarea row used by the scene editor.
function PlanEditField({
  label,
  value,
  onChange,
  rows,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows: number
}) {
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)] gap-2">
      <span className="pt-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} />
    </div>
  )
}

function SceneField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)] gap-2">
      <span className="pt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="whitespace-pre-wrap text-xs leading-relaxed text-card-foreground">
        {children}
      </span>
    </div>
  )
}

function MetaCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card>
      <Card.Body className="px-3 py-3">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-sm text-card-foreground">{children}</p>
      </Card.Body>
    </Card>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
